import type { Command } from '../../types/commands'
import { formatCommand, colorize } from '../colorScheme'
import { commandRegistry } from './index'

export const helpCommand: Command = {
name: 'help',
description: 'Display available commands',
aliases: ['?', 'commands'],
usage: 'help [command]',
category: 'system',
execute: (args) => {
// Show detailed help for specific command
if (args.length > 0) {
const cmdName = args[0].toLowerCase()
const command = commandRegistry[cmdName] ||
Object.values(commandRegistry).find(cmd => cmd.aliases?.includes(cmdName))

if (!command) {
return [
{
type: 'error',
content: `No help available for: ${cmdName}\n\nType ${colorize('help', 'yellow')} to see all available commands.`,
},
]
}

const aliases = command.aliases
? `\n${colorize('Aliases:', 'cyan')} ${command.aliases.join(', ')}`
: ''

const usage = command.usage
? `\n${colorize('Usage:', 'cyan')} ${command.usage}`
: ''

const detailedHelp = `
╭─ ${colorize(command.name.toUpperCase(), 'brightCyan')} ─────────────────────────────────────╮
│                                                           │
│  ${colorize('Description:', 'cyan')} ${command.description}${' '.repeat(Math.max(0, 38 - command.description.length))}│${aliases ? `│  ${aliases}${' '.repeat(Math.max(0, 52 - aliases.length))}│` : ''}${usage ? `│  ${usage}${' '.repeat(Math.max(0, 52 - usage.length))}│` : ''}
│                                                           │
╰───────────────────────────────────────────────────────────╯
`

return [
{
type: 'info',
content: <div dangerouslySetInnerHTML={{ __html: detailedHelp }} />,
},
]
}

// Helper to format command line
const cmd = (name: string, desc: string) => {
const paddedName = name.padEnd(13)
return `│  ${formatCommand(paddedName)} - ${desc} │`
}

// Pre-build complex command descriptions
const helpDesc = `Show this help message ${colorize('(aliases: ?, commands)', 'brightBlack')}`
const clearDesc = `Clear terminal output ${colorize('(alias: cls)', 'brightBlack')}           `
const themeDesc = `Switch theme ${colorize('(professional/horror)', 'brightBlack')}           `

// Build colored help text
const helpText = `
╔════════════════════════════════════════════════════════════╗
║                   ${colorize('AVAILABLE COMMANDS', 'brightCyan')}                       ║
╚════════════════════════════════════════════════════════════╝

┌─ ${colorize('NAVIGATION & INFORMATION', 'yellow')} ────────────────────────────────────┐
${cmd('about', 'Learn about Anderson Reinkordt              ')}
${cmd('projects', 'View portfolio projects                     ')}
${cmd('skills', 'Display technical skills and expertise      ')}
${cmd('experience', 'Show work experience                        ')}
${cmd('contact', 'Get contact information                     ')}
${cmd('blog', 'Read thought log entries                    ')}
└───────────────────────────────────────────────────────────────┘

┌─ ${colorize('SYSTEM COMMANDS', 'yellow')} ──────────────────────────────────────────────┐
${cmd('help', helpDesc)}
${cmd('tutorial', `Interactive tutorial for new users           `)}
${cmd('clear', clearDesc)}
${cmd('theme [mode]', themeDesc)}
${cmd('settings', `Adjust accessibility settings                `)}
└────────────────────────────────────────────────────────────────┘

╭───────────────────────────────────────────────────────────╮
│  ${colorize('💡 TIPS & SHORTCUTS', 'brightYellow')}                                      │
├───────────────────────────────────────────────────────────┤
│  ${colorize('Navigation:', 'cyan')}                                              │
│  • Use ${colorize('Tab', 'yellow')} for autocomplete                               │
│  • Use ${colorize('↑/↓', 'yellow')} arrows (or ${colorize('Ctrl+P/Ctrl+N', 'yellow')}) to navigate history  │
│  • Press ${colorize('Escape', 'yellow')} to clear current input                    │
│                                                           │
│  ${colorize('Accessibility:', 'cyan')}                                           │
│  • Type ${formatCommand('settings')} to adjust font size & contrast           │
│  • Type ${formatCommand('settings font large')} for larger text               │
│  • Type ${formatCommand('settings contrast high')} for high contrast          │
│  • Your preferences are saved across sessions             │
│                                                           │
│  ${colorize('Exploration:', 'cyan')}                                             │
│  • Type ${formatCommand('help [command]')} for detailed command info          │
│  • Try different commands to explore the terminal         │
│  • Some commands have hidden features - experiment!       │
╰───────────────────────────────────────────────────────────╯

${colorize('>> Type any command above to get started!', 'green')}
`

    return [
      {
        type: 'info',
        content: (
          <div dangerouslySetInnerHTML={{ __html: helpText }} />
        ),
      },
    ]
  },
}
